import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_SERVICE } from "../Services/CommonApi";
import {
  GET_FEATURE_TOGGLES,
  SET_FEATURE_TOGGLES,
} from "../constant/CommonApiUrl";

const FEATURE_TOGGLE_MESSAGES = {
  UNKNOWN_TOGGLE: "One of these settings is no longer recognized. Refresh and try again.",
  INVALID_VALUE: "One of these settings has an invalid value.",
  DUPLICATE_TOGGLE: "The same setting was included more than once.",
  WRITE_FAILED: "The server couldn't save the feature settings.",
  400: "Select at least one setting to update.",
};

export const getFeatureToggleMessage = (messageCode, fallbackMessage) =>
  FEATURE_TOGGLE_MESSAGES[messageCode] ||
  fallbackMessage ||
  "The feature settings couldn't be updated.";

export const fetchFeatureToggles = createAsyncThunk(
  "featureToggle/fetch",
  async (_, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_FEATURE_TOGGLES);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveFeatureToggles = createAsyncThunk(
  "featureToggle/save",
  async (changes, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(SET_FEATURE_TOGGLES, changes);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

const initialState = {
  snapshot: null,
  loadStatus: "idle",
  saveStatus: "idle",
  error: null,
};

const featureToggleSlice = createSlice({
  name: "featureToggle",
  initialState,
  reducers: {
    clearFeatureToggleError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureToggles.pending, (state) => {
        state.loadStatus = "loading";
        state.error = null;
      })
      .addCase(fetchFeatureToggles.fulfilled, (state, action) => {
        state.loadStatus = "loaded";
        if (action.payload?.status) {
          state.snapshot = action.payload.data;
        } else {
          state.error = {
            messageCode: action.payload?.messageCode,
            message: getFeatureToggleMessage(
              action.payload?.messageCode,
              action.payload?.message,
            ),
          };
        }
      })
      .addCase(fetchFeatureToggles.rejected, (state, action) => {
        state.loadStatus = "error";
        state.error = {
          messageCode: null,
          message: action.payload || "The feature settings couldn't be loaded.",
        };
      })
      .addCase(saveFeatureToggles.pending, (state) => {
        state.saveStatus = "loading";
        state.error = null;
      })
      .addCase(saveFeatureToggles.fulfilled, (state, action) => {
        state.saveStatus = "idle";
        if (action.payload?.data) {
          state.snapshot = action.payload.data;
        }
        if (!action.payload?.status) {
          state.error = {
            messageCode: action.payload?.messageCode,
            message: getFeatureToggleMessage(
              action.payload?.messageCode,
              action.payload?.message,
            ),
          };
        }
      })
      .addCase(saveFeatureToggles.rejected, (state, action) => {
        state.saveStatus = "idle";
        state.error = {
          messageCode: null,
          message: action.payload || "The feature settings couldn't be updated.",
        };
      });
  },
});

export const { clearFeatureToggleError } = featureToggleSlice.actions;
export default featureToggleSlice.reducer;
